import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users as UsersIcon } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { MIKROTIK_GROUP_OPTIONS } from './radiusUtils';

const EMPTY = { groupname: '', mikrotik_group: 'read', description: '' };

export default function RadiusGroups({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delGroup, setDelGroup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/radius/groups'); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged && onChanged(); };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ groupname: it.groupname, mikrotik_group: it.mikrotik_group || 'read', description: it.description || '' }); setOpen(true); };

  const save = async () => {
    if (!editing && !form.groupname) return toast.error('Nama group wajib diisi');
    setSaving(true);
    try {
      if (editing) { await api.put(`/radius/groups/${encodeURIComponent(editing.groupname)}`, { mikrotik_group: form.mikrotik_group, description: form.description }); toast.success('Group diperbarui'); }
      else { await api.post('/radius/groups', { groupname: form.groupname, mikrotik_group: form.mikrotik_group, description: form.description }); toast.success('Group dibuat'); }
      setOpen(false); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/radius/groups/${encodeURIComponent(delGroup.groupname)}`); toast.success('Group dihapus'); setDelGroup(null); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Group memetakan atribut <span className="font-mono">Mikrotik-Group</span> (full/write/read) via radgroupreply.</p>
        <Button onClick={openAdd} data-testid="radius-group-add"><Plus className="w-4 h-4 mr-1.5" /> Add Group</Button>
      </div>
      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['Group Name','Mikrotik-Group','Members','Description','Action'].map((h) => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-muted-foreground">Belum ada group. Buat FULL / WRITE / READ.</TableCell></TableRow>)}
              {!loading && items.map((it) => (
                <TableRow key={it.groupname} data-testid={`radius-group-row-${it.groupname}`}>
                  <TableCell className="text-sm font-medium">{it.groupname}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] font-mono">{it.mikrotik_group || '—'}</Badge></TableCell>
                  <TableCell className="text-xs"><span className="inline-flex items-center gap-1 text-muted-foreground"><UsersIcon className="w-3 h-3" /> {it.members}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.description || '—'}</TableCell>
                  <TableCell><div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => openEdit(it)} data-testid={`radius-group-edit-${it.groupname}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-rose-500" onClick={() => setDelGroup(it)} data-testid={`radius-group-del-${it.groupname}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="radius-group-form">
          <SheetHeader><SheetTitle>{editing ? 'Edit Group' : 'Add Group'}</SheetTitle><SheetDescription>Mapping: FULL→full, WRITE→write, READ→read.</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs">Group Name</Label><div className="mt-1"><Input value={form.groupname} disabled={!!editing} onChange={(e) => setForm({ ...form, groupname: e.target.value })} placeholder="FULL" data-testid="radius-group-f-name" /></div></div>
            <div><Label className="text-xs">Mikrotik-Group</Label><div className="mt-1">
              <Select value={form.mikrotik_group} onValueChange={(v) => setForm({ ...form, mikrotik_group: v })}>
                <SelectTrigger data-testid="radius-group-f-mikrotik"><SelectValue /></SelectTrigger>
                <SelectContent>{MIKROTIK_GROUP_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select></div></div>
            <div><Label className="text-xs">Description</Label><div className="mt-1"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Full access router" data-testid="radius-group-f-desc" /></div></div>
          </div>
          <SheetFooter className="mt-4"><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="radius-group-save">{saving ? 'Menyimpan…' : 'Simpan'}</Button></SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delGroup} onOpenChange={(o) => !o && setDelGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus Group?</AlertDialogTitle><AlertDialogDescription>Group <span className="font-mono">{delGroup?.groupname}</span> akan dihapus. Tidak bisa dihapus bila masih dipakai user.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="radius-group-del-confirm">Ya, Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
